'use client';

import { useQuery } from '@tanstack/react-query';

import { TrailerGate } from '@cineamo/api/models/movie-trailer/TrailerPlayback.types';

import { GATED_COUNTRY, LegalClock } from '@cineamo/api/helpers/trailer-playback-helper';

/**
 * How often the server clock is re-read, in ms.
 *
 * It exists for the window boundary, not for accuracy: without it a player left open at 21:55 is
 * still refusing to play at 22:30, and one opened at 05:45 keeps playing past the close. A minute is
 * the coarsest interval that still lands the boundary within the minute it is named in.
 *
 * One request per minute per open tab, served by a route handler that formats a `Date` and calls
 * nothing — and only on surfaces that actually carry a gated trailer, since a hook with no gate
 * never enables the query.
 */
const REFETCH_INTERVAL_MS = 60_000;

const legalClockKey = ['legal-clock', GATED_COUNTRY] as const;

/**
 * The server's reading of the German wall clock, `"22:15"`, or `null` until it arrives (CIN-14724).
 *
 * The § 5 Abs. 4 JMStV window is judged on this and never on the device clock — feed it straight to
 * `resolveTrailerPlayback()`. `null` blocks a time-gated trailer, which is the safe direction: a
 * viewer who could defeat the gate by moving their own clock is the hole this closes.
 *
 * Keyed on the country and not on the caller, so the many gated trailers on one page share a single
 * request — a reel of twenty slides polls once, not twenty times.
 *
 * @param gate Pass the gate the caller holds, or `undefined` when it has none. Nothing is fetched
 *     for an ungated surface or an ungated country: those never consult a clock, so a Storybook
 *     story and an Austrian cinema website both make no request at all.
 */
export function useLegalClock(gate?: TrailerGate): LegalClock | null {
    const { data } = useQuery({
        queryKey: legalClockKey,
        queryFn: async (): Promise<LegalClock | null> => {
            const response = await fetch('/api/legal-clock');
            if (!response.ok) return null;
            const body: { time?: unknown } = await response.json();
            return typeof body.time === 'string' ? body.time : null;
        },
        enabled: gate?.countryCode === GATED_COUNTRY,
        refetchInterval: REFETCH_INTERVAL_MS,
        // The reading is worth exactly as long as the interval that replaces it; anything longer
        // would serve a stale minute across the boundary the interval exists to catch.
        staleTime: REFETCH_INTERVAL_MS,
        // A failed read blocks trailers, so it is worth retrying — but briefly, and never so long
        // that a viewer sits in front of a notice waiting for a route handler that is not coming.
        retry: 2
    });

    return data ?? null;
}
